import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { resolveScreenId } from '@/shell/modules';

/**
 * What a screen should do on arrival, when another screen sent the user
 * there for a reason: "receive more of this item into this store", "show me
 * this store's stock". Ids are strings, as everywhere else in the web app.
 */
export interface ScreenIntent {
  action?: 'create';
  storeId?: string;
  skuId?: string;
  /** How that item reads ("F-51-01 Broiler Starter Crumble"), for a screen to name it before its list loads. */
  skuLabel?: string;
  batchId?: string;
  /** Narrow a list rather than open a form. */
  filterStoreId?: string;
  /** Open this record on arrival: an employee, a pay run, an advance. */
  recordId?: string;
  /** Narrow a list to one person. */
  employeeId?: string;
  /** Open on one farm (or HEAD_OFFICE), e.g. its attendance register. */
  farmId?: string;
  /** Open a list on a named view, e.g. only the stock running short. */
  view?: ScreenView;
  /**
   * Load work saved in the outbox back into the form, to put right what the
   * server refused (.docs/023, Needs attention).
   */
  fixOperation?: string;
}

export type ScreenView = 'low';
const SCREEN_VIEWS: readonly string[] = ['low'] satisfies ScreenView[];

/**
 * The Stock screen narrowed to the items running short: where the dashboard's
 * card, its Lowest stock chart and the "Items running short" action all land,
 * so each shows the items it counted rather than all stock.
 */
export const RUNNING_SHORT: ScreenIntent = { view: 'low' };

interface ShellNavState {
  /** Replaced by App.tsx once mounted, with its real navigate(). */
  navigate: (screenId: string, intent?: ScreenIntent) => void;
  pendingIntent: { screenId: string; intent: ScreenIntent } | null;
}

/**
 * Lets a screen switch to a different screen (e.g. the hierarchy tree jumping
 * from a shed node to the Sheds table) without prop-drilling `onNavigate`
 * down through Shell into every screen component. App.tsx keeps this pointed
 * at its own navigate(), which already knows about "open in new window".
 */
export const useShellNav = create<ShellNavState>(() => ({
  navigate: () => {},
  pendingIntent: null,
}));

const INTENT_KEYS = ['action', 'storeId', 'skuId', 'skuLabel', 'batchId', 'filterStoreId', 'recordId', 'employeeId', 'farmId', 'view', 'fixOperation'] as const;

/** Encodes an intent as query parameters, for a standalone window's URL. */
export function intentToQuery(intent: ScreenIntent | undefined): string {
  if (!intent) return '';
  const params = new URLSearchParams();
  for (const key of INTENT_KEYS) {
    const value = intent[key];
    if (value) params.set(key, value);
  }
  const text = params.toString();
  return text ? `&${text}` : '';
}

export function intentFromQuery(params: URLSearchParams): ScreenIntent | null {
  const intent: ScreenIntent = {};
  for (const key of INTENT_KEYS) {
    const value = params.get(key);
    if (!value) continue;
    if (key === 'action') {
      if (value === 'create') intent.action = 'create';
    } else if (key === 'view') {
      // A hand-edited URL with an unknown view opens the plain list.
      if (SCREEN_VIEWS.includes(value)) intent.view = value as ScreenView;
    } else {
      intent[key] = value;
    }
  }
  return Object.keys(intent).length > 0 ? intent : null;
}

/**
 * The intent another screen left for `screenId`, read once on mount and then
 * cleared, so returning to the screen later through the sidebar does not
 * re-open the same prefilled form.
 *
 * An intent sent to a folded-away screen id (the dashboard still says
 * `inv-stock`) belongs to the screen that id now lands on.
 */
export function useScreenIntent(screenId: string): ScreenIntent | null {
  const [intent] = useState<ScreenIntent | null>(() => pendingIntentFor(screenId));

  useEffect(() => {
    if (pendingIntentFor(screenId)) useShellNav.setState({ pendingIntent: null });
  }, [screenId]);

  return intent;
}

function pendingIntentFor(screenId: string): ScreenIntent | null {
  const pending = useShellNav.getState().pendingIntent;
  return pending && resolveScreenId(pending.screenId) === screenId ? pending.intent : null;
}
